import IconText from "app/components/IconText";
import { relationTypeOptions } from "app/containers/Features/containers/Ticket/container/Relations/relationTypeOptions";
import workflowIcon from "assets/svgs/ic_tree.svg";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomPopover from "./CustomPopover";
import TicketTypeIcon from "app/components/TicketTypeIcon";
import { ITicketRelation, ITickets } from "app/containers/Features/containers/Boards/containers/Tickets/types";
import { TicketRelationTypeQuery } from "app/containers/Features/containers/Ticket/container/Relations/types";

interface RelationsProps {
  data: ITickets;
}

const HOVER_CLOSE_DELAY = 120;
const RELATION_TYPE_BORDER_COLORS: Record<string, string> = {
  [TicketRelationTypeQuery.BLOCKED_BY]: "#FA3F77",
  [TicketRelationTypeQuery.BLOCKS]: "#6B7DF8",
  [TicketRelationTypeQuery.DUPLICATED]: "#D16DEA",
  [TicketRelationTypeQuery.PARENT]: "#F2994A",
  [TicketRelationTypeQuery.CHILD]: "#F2C94C",
  [TicketRelationTypeQuery.LINKED_TICKET]: "#2CBB93",
};

const Relations: React.FC<RelationsProps> = ({ data }) => {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleHoverStart = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const handleHoverEnd = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, HOVER_CLOSE_DELAY);
  };

  useEffect(() => () => clearCloseTimeout(), []);

  const relations = useMemo(
    () =>
      (data?.relations ?? []).filter(
        (item): item is ITicketRelation =>
          item != null &&
          typeof item === "object" &&
          typeof item._id === "string" &&
          item.ticket != null &&
          typeof item.ticket._id === "string",
      ),
    [data?.relations],
  );

  const groupedRelations = useMemo(() => {
    const groups = new Map<string, ITicketRelation[]>();

    relations.forEach((relation) => {
      const key = relation.relationType;
      const existing = groups.get(key) ?? [];
      existing.push(relation);
      groups.set(key, existing);
    });

    return Array.from(groups.entries());
  }, [relations]);

  return (
    <CustomPopover
      show={open}
      onClose={(val) => setOpen(val)}
      trigger={
        <div
          className="flex flex-row items-center rounded px-1 py-[1px] hover:bg-gray-200"
          onMouseEnter={handleHoverStart}
          onMouseLeave={handleHoverEnd}
        >
          <div className="min-w-[16px]">
            <img src={workflowIcon} alt="relations" className="size-[16px]" />
          </div>
          <div className="text-xs leading-[18px] text-secondary font-normal mt-[2px] ml-[2px]">
            {relations.length}
          </div>
        </div>
      }
      content={
        <div
          className="box-border flex max-h-[312px] flex-col gap-[10px] py-4 px-3"
          onMouseEnter={handleHoverStart}
          onMouseLeave={handleHoverEnd}
        >
          <p className="shrink-0 text-base font-medium leading-normal text-[#253858]">
            {relations.length} relation{relations.length > 1 ? "s" : ""}:
          </p>
          <div className="no-visible-bar flex max-h-[246px] flex-col gap-[10px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none]">
            {groupedRelations.map(([relationType, items]) => {
              const relationOption = relationTypeOptions.find(
                (option) => option.value === relationType,
              );

              return (
                <div
                  key={relationType}
                  className="flex shrink-0 flex-col gap-1 rounded border border-l-4 p-2"
                  style={{
                    borderColor:
                      RELATION_TYPE_BORDER_COLORS[relationType] ?? "#79808A",
                    borderLeftColor:
                      RELATION_TYPE_BORDER_COLORS[relationType] ?? "#79808A",
                  }}
                >
                  <IconText
                    icon={relationOption?.icon}
                    text={`${relationOption?.label ?? relationType}:`}
                    textClassName="text-sm font-medium text-[#253858]"
                  />
                  {items.map((relation) => (
                    <div
                      key={relation._id}
                      className="flex min-w-0 items-center gap-1"
                    >
                      <TicketTypeIcon
                        iconName={relation.ticket.type?.icon}
                        size={16}
                      />
                      <span className="min-w-0 truncate text-sm text-secondary">
                        {relation.ticket.ticketID}:{" "}
                        {relation.ticket.ticketName}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      }
      className="!max-h-[312px] !w-[415px] !overflow-hidden !rounded-[4px] !p-0 shadow-md"
    />
  );
};

export default Relations;
